<template>
  <div class="h-full w-[300px] border-r border-t border-gray-200">
    <div class="p-4 pt-4 h-full flex flex-col w-full">
      <div></div>
      <div class="mb-4" v-if="merchants.length && merchants.length > 1">
        <Select v-model="activeMerchantId">
          <SelectTrigger>
            <SelectValue placeholder="Select a merchant" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel>Select Account</SelectLabel>
              <SelectItem v-for="merchant of merchants" :key="merchant._id" :value="merchant._id!">
                {{ merchant.name }}
              </SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
      <ul class="flex flex-col w-full list-none m-0 p-0 text-gray-500 font-semibold">
        <li v-for="option in sideMenuTopOptions" :key="option.name">
          <router-link
            :to="option.to"
            class="flex items-center gap-2 p-2 rounded-lg hover:text-grey-light-800 hover:bg-grey-light-50 min-h-[42px]"
            :class="{
              'pl-4': option.name === 'Cancellations',
              'text-grey-light-800 bg-grey-light-50': isActiveRoute(option.to),
            }"
            @click="closeSidebar"
          >
            <component :is="option.icon" class="h-4 w-4 text-grey-light-500" />
            <span class="text-grey-light-700 text-sm font-semibold">{{ option.name }}</span>
            <div
              v-if="option.name === 'Boost Rewards'"
              class="flex gap-1 items-center ml-auto rounded-md bg-success-100 font-semibold text-success-500 text-xs px-2 py-1"
            >
              <Sparkles class="h-3 w-3" />
              New
            </div>
            <LabelComponent
              v-if="option.name === 'Cancellations' && unresolvedCancellationCount != null"
              class="flex items-center justify-center ml-auto min-w-6 h-6 rounded-lg text-xs text-grey-light-500 border-grey-light-200 bg-grey-light-500 text-white"
            >
              {{ unresolvedCancellationCount }}
            </LabelComponent>
          </router-link>
        </li>
      </ul>

      <ul class="flex flex-col w-full list-none m-0 p-0 text-gray-500 font-semibold mt-auto">
        <li v-for="option in sideMenuBottomOptions" :key="option.name">
          <router-link
            v-if="option.to !== '' && !option.hide"
            :to="option.to"
            class="flex items-center gap-2 p-2 rounded-lg hover:text-grey-light-800 hover:bg-grey-light-50"
            active-class="text-grey-light-800 bg-grey-light-50"
            @click="closeSidebar"
          >
            <component :is="option.icon" class="h-4 w-4 text-gray-500" />
            <span class="text-gray-700 text-sm">{{ option.name }}</span>
          </router-link>
          <span
            v-else-if="!option.hide"
            class="flex items-center gap-2 p-2 rounded-lg hover:text-grey-light-800 hover:bg-grey-light-50 hover:cursor-pointer"
            @click="showModal(option.name)"
          >
            <component :is="option.icon" class="h-4 w-4 text-gray-500" />
            <span class="text-gray-700 text-sm">{{ option.name }}</span>
          </span>
        </li>
      </ul>
      <div class="border border-gray-200 my-4"></div>
      <div class="flex gap-2 items-center">
        <div
          @click="emit('show-profile', true)"
          class="w-10 h-10 flex items-center justify-center rounded-full bg-brand-700 text-white shrink-0 hover:cursor-pointer"
        >
          {{ initials }}
        </div>
        <div class="flex w-full justify-between items-start">
          <div @click="emit('show-profile', true)" class="hover:cursor-pointer">
            <h6 class="font-semibold text-sm text-gray-700">
              {{ userStore.user?.name || '' }}
            </h6>
            <p class="text-sm text-gray-500 max-w-[190px] truncate">
              {{ userStore.user?.email || '' }}
            </p>
          </div>
          <Button data-testid="sign-out" size="icon" variant="ghost" type="button" @click="onSignOut">
            <LogOut class="w-5 h-5 text-grey-light-600" />
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ENVIRONMENT } from '@/config';
import { useCancellationStore } from '@/stores/cancellations';
import { useMerchantStore } from '@/stores/merchants';
import { useUserStore } from '@/stores/users';
import { getInitials } from '@/utils/text.utils';
import {
  Ban,
  ChartPie,
  CopyCheck,
  CreditCard,
  House,
  LifeBuoy,
  LogOut,
  Phone,
  PiggyBank,
  Settings,
  Sparkles,
  Wallet,
} from 'lucide-vue-next';
import { onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { ModelMerchants } from '@layup/api-sdk/dist/api';
import LabelComponent from '../ui/label/LabelComponent.vue';

const props = defineProps({
  isMenuOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close-sidebar', 'show-profile', 'show-contact-modal', 'show-support-modal']);

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
const merchantStore = useMerchantStore();
const cancellationStore = useCancellationStore();
const hideSettingsMenuItem = ref(true);
const merchants = ref<ModelMerchants[]>([]);
const activeMerchantId = ref('');
const initials = ref('');
const unresolvedCancellationCount = ref<null | number>(null);

const ordersMenuItems = [
  {
    to: '/merchant/orders',
    name: 'Orders',
    icon: CreditCard,
  },
  {
    to: '/merchant/cancellations',
    name: 'Cancellations',
    icon: Ban,
  },
];

const boostMenuItems = [
  {
    to: '/merchant/orders?type=BoostRewards',
    name: 'Boost Rewards',
    icon: PiggyBank,
  },
];

const sideMenuTopOptions = ref([
  {
    to: '/merchant',
    name: 'Home',
    icon: House,
  },
  ...ordersMenuItems,
  {
    to: '/merchant/payments',
    name: 'Payments',
    icon: Wallet,
  },
  {
    to: '/merchant/invoices',
    name: 'Invoices',
    icon: CopyCheck,
  },
  {
    to: '/merchant/settlements',
    name: 'Settlements',
    icon: ChartPie,
  },
]);

const sideMenuBottomOptions = ref([
  {
    to: '',
    name: 'Support',
    icon: LifeBuoy,
    hide: false,
  },
  {
    to: '',
    name: 'Contact Us',
    icon: Phone,
    hide: false,
  },

  {
    to: '/merchant/settings',
    name: 'Settings',
    icon: Settings,
    hide: true,
  },
]);

const showModal = (name: string) => {
  if (name === 'Contact Us') emit('show-contact-modal');
  if (name === 'Support') emit('show-support-modal');
};

const syncSettingsMenuVisibility = () => {
  const settingsOption = sideMenuBottomOptions.value.find((option) => option.name === 'Settings');

  if (settingsOption) {
    settingsOption.hide = hideSettingsMenuItem.value;
  }
};

const closeSidebar = () => {
  if (props.isMenuOpen) {
    emit('close-sidebar');
  }
};

const onSignOut = async () => {
  merchantStore.onResetMerchant();
  await userStore.signOut();
  await router.push({ name: 'merchantSignIn' });
};

// This function was created to make sure /orders and /orders?type=boostRewards don't both get the active class on either route
const isActiveRoute = (routeTo: string): boolean => {
  const [path, queryString] = routeTo.split('?');

  if (route.path !== path) {
    return false;
  }

  if (queryString) {
    const params = new URLSearchParams(queryString);
    for (const [key, value] of params) {
      if (route.query[key] !== value) {
        return false;
      }
    }

    return true;
  }

  // If menu item has no query params, only match if route also has none
  // This prevents /merchant/orders from being active when on /merchant/orders?type=BoostRewards
  return Object.keys(route.query).length === 0;
};

onMounted(async () => {
  const user = userStore.user;

  if (user && user.name) {
    initials.value = getInitials(user.name);
  } else {
    initials.value = 'LT';
  }

  syncSettingsMenuVisibility();

  await cancellationStore.fetchUnresolvedCancellations();
});

watch(
  () => userStore.user,
  (newUser) => {
    hideSettingsMenuItem.value = newUser?.merchantRole !== 'ADMIN';
    syncSettingsMenuVisibility();

    if (newUser?.merchants?.length) {
      merchants.value = newUser.merchants;

      const merchantExists = newUser.merchants.some((m) => m._id === merchantStore.activeMerchantId);

      if (merchantStore.activeMerchantId && merchantExists) {
        activeMerchantId.value = merchantStore.activeMerchantId;
      } else {
        activeMerchantId.value = '';
      }
    }
  },
  { immediate: true },
);

if (ENVIRONMENT !== 'production') {
  watch(
    () => merchantStore.merchant,
    (newMerchant) => {
      if (newMerchant) {
        const options = sideMenuTopOptions.value;
        const boostMenuItemExists = options.some((option) => option.name === 'Boost Rewards');

        if (newMerchant.voucherSettings?.active) {
          if (!boostMenuItemExists) {
            options.splice(1, ordersMenuItems.length, ...boostMenuItems);
          }
        } else {
          if (boostMenuItemExists) {
            options.splice(1, boostMenuItems.length, ...ordersMenuItems);
          }
        }
      }
    },
    { immediate: true },
  );
}

watch(activeMerchantId, (newMerchantId) => {
  if (newMerchantId) {
    merchantStore.onChangeMerchant(newMerchantId);
    // Redirect to home page to refresh the dashboard with the new merchant
    router.push({ name: 'merchantDashboardHome' });
  }
});

watch(
  () => cancellationStore.unresolvedCancellations,
  (unresolvedCancellations) => {
    unresolvedCancellationCount.value = unresolvedCancellations ? unresolvedCancellations.length : null;
  },
);
</script>
